import { 
    Injectable, NgModuleFactoryLoader, ApplicationRef, 
    NgModule, NgModuleFactory, Compiler, 
    ComponentFactoryResolver, Injector
} from '@angular/core';
import { Location } from '@angular/common';

import { BehaviorSubject, Observable, Observer } from 'rxjs';

import { Router, NavigationStart, NavigationEnd, NavigationCancel } from '@angular/router';
import { AppConfig } from './app-config';
import { Logger } from './logger';
import { environment } from '@tytapp/environment';

export interface SiteWideAlert {
    id : string;
    type? : 'info' | 'warning' | 'danger';
    message : string;
    url? : string;
    internal? : boolean;
    backgroundColor? : string;
    textColor? : string;
    onDismiss? : () => void;
}

@Injectable()
export class Shell {
    constructor(
        private router : Router,
        private location : Location,
        private appConfig : AppConfig,
        private loader: NgModuleFactoryLoader,
        private appRef : ApplicationRef,
        private compiler : Compiler,
        private componentFactoryResolver : ComponentFactoryResolver,
        private injector : Injector
    ) {
        router.events.subscribe(ev => {
            if (ev instanceof NavigationStart) {
                this.isChrome = false;
                this.noLayout = false;
                
                this._urlHistory.push(ev.url);
                if (this._urlHistory.length > 5)
                    this._urlHistory = this._urlHistory.slice(this._urlHistory.length - 5);

            } else if (ev instanceof NavigationEnd) {
                this._firstRoute = false;
            }
        });

        this.dialogShake = Observable.create(observer => this._dialogShaker = observer)
    }

    private _urlHistory : string[] = [];

    get urlHistory() {
        return this._urlHistory.slice();
    }

    public async sendSyntheticPageView(url : string): Promise<void> {
        await new Promise<void>((resolve, reject) => {
            try {
                if (typeof window !== 'undefined' && window['dataLayer']) {
                    setTimeout(() => {
                        resolve();
                    }, 4000);

                    window['dataLayer'].push({ 
                        'event': 'Synthesized Page View',
                        'target': 'Event',
                        'spv-content-name': url,
                        'eventCallback': () => resolve(),
                        'eventTimeout': 4000
                    });
                } else {
                    console.warn(`Cannot send synthetic page view for '${url}': dataLayer not present`);
                    resolve();
                }
            } catch (e) {
                console.error(`Caught error while sending SPV`);
                console.error(e);
            }
        });
    }

    public get features() {
        return this.appConfig.appStatus.features;
    }

    public async hasFeature(name : string) {
        return await this.appConfig.featureEnabled(name);
    }

    public get firstRoute() {
        return this._firstRoute;
    }

    private _alerts : SiteWideAlert[] = [];
    private _alertsChanged = new BehaviorSubject<SiteWideAlert[]>([]);

    get alerts() {
        return this._alerts;
    }

    public get alertsChanged() {
        return this._alertsChanged;
    }

    addAlert(alertObj : SiteWideAlert) {
        let filteredAlerts = this._alerts.filter(x => x.id !== alertObj.id);

        filteredAlerts.push(alertObj);

        this._alerts = filteredAlerts;
        this._alertsChanged.next(this._alerts);
    }

    removeAlert(id : string) {
        let filteredAlerts = this._alerts.filter(x => x.id !== id);
        this._alerts = filteredAlerts;
        this._alertsChanged.next(this._alerts);
    }

    dismissAlert(id : string) {
        let alert = this._alerts.find(x => x.id === id);

        this.removeAlert(id);

        if (alert.onDismiss) {
            alert.onDismiss();
        }
    }

    private _firstRoute : boolean = true;
    private _isChrome : boolean = false;
    private _noLayout : boolean = false;
    private _dialogShaker : Observer<void>;

    isChromeChanged : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    noLayoutChanged : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    dialogChanged : BehaviorSubject<any> = new BehaviorSubject<any>(null);
    dialogShake : Observable<void>;

    private _dialog : any = null;

    get dialog(): any {
        return this._dialog;
    }

    static namedDialogs : any = {};

    /**
     * Register a named dialog. This is useful when a pair of dialogs must reference each other.
     * @param name 
     * @param cls 
     */
    static registerNamedDialog(name : string, cls : Function) {
        this.namedDialogs[name] = cls;
    }

    /**
     * Retrieve a named dialog 
     * @param nameOrClass 
     */
    static getDialogByName(name : string) {
        return this.namedDialogs[name];
    }

    static getNameForDialog(cls : any) {
        let pair = Object.keys(this.namedDialogs)
            .map(key => [key, this.namedDialogs[key]])
            .find(pair => pair[1] === cls)
        ;

        if (pair)
            return pair[0];
        
        return null;
    }

    shakeDialog() {
        this._dialogShaker.next(null);
    }

    dialogInstantly : boolean = false;

    set dialog(value : any) {
        this._dialog = value;
        this.dialogChanged.next(value);
    }

    private _dynamicDialogContexts : any = {};

    /**
     * Retrieve the appropriate component factory resolver and injector 
     * for the given dialog. If no specific context is found, the root (app)
     * context is returned.
     * @param clsName 
     */
    public getDynamicDialogContext(dialogCls) {
        if (this._dynamicDialogContexts[dialogCls])
            return this._dynamicDialogContexts[dialogCls];

        return [this.componentFactoryResolver, this.injector];
    }

    animateToDialog(animation : string, cls : Function, ...args) {
        this.dialog = [cls, args, animation];
    }

    async showDialog(cls : Function, ...args) {
        this.dialogInstantly = false;
        this.dialog = [cls, args];
    }

    hideDialog() {
        this.dialog = null;
    }

    set noLayout(value : boolean) {
        this._noLayout = value;
        this.noLayoutChanged.next(this._noLayout);
    }

    get noLayout() {
        return this._noLayout;
    }

    get isChrome() {
        return this._isChrome;
    }

    public get rootUrl() : string { return environment.urls.root; }
    public get platformUrl() : string { return environment.urls.platform; }

    set isChrome(value : boolean) {
        this._isChrome = value;

        if (typeof window !== 'undefined' && window.document) {
            if (value) {
                window.document.documentElement.classList.add("chrome");        
            } else {
                window.document.documentElement.classList.remove("chrome");
            }
        }

        this.isChromeChanged.next(this._isChrome);
    }
}